import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import {
  adminListPosts,
  adminDeletePost,
  adminGenerateDraft,
  adminBulkPostAction,
} from "@/lib/blog-admin.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { DAY_LABELS, plannedDayFor, recommendedSlot } from "@/lib/posting-schedule";
import { Plus, Trash2, ExternalLink, Search, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/posts/")({
  component: AdminPostsList,
  head: () => ({ meta: [{ title: "Blog posts · Admin" }, { name: "robots", content: "noindex" }] }),
});

function parsePlannedDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatPlannedDate(value: string, options: Intl.DateTimeFormatOptions) {
  const date = parsePlannedDate(value);
  return date?.toLocaleDateString(undefined, { ...options, timeZone: "UTC" }) ?? value;
}

function sundayStart(value: Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - date.getDay());
  return date;
}

function AdminPostsList() {
  const fetchPosts = useServerFn(adminListPosts);
  const deletePost = useServerFn(adminDeletePost);
  const generateDraft = useServerFn(adminGenerateDraft);
  const bulkAction = useServerFn(adminBulkPostAction);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft" | "scheduled" | "archived">("all");
  const [periodFilter, setPeriodFilter] = useState<"all" | "this-week" | "next-week" | "last-week">("all");
  const [laneFilter, setLaneFilter] = useState<"all" | "pillar" | "supporting" | "gsc" | "featured">("all");
  const [aiBusy, setAiBusy] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDay, setBulkDay] = useState<number>(1);

  // Sunday-start boundaries match the weekly blog plan shown in the table.
  // Compare planned dates as UTC date keys so the week's Sunday is never
  // excluded by local timezone offsets (planned_date is a UTC calendar day).
  const now = new Date();
  const startOfThisWeek = sundayStart(now);
  const startOfLastWeek = new Date(startOfThisWeek);
  startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
  const dateKeyOf = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const startOfNextWeek = new Date(startOfThisWeek);
  startOfNextWeek.setDate(startOfNextWeek.getDate() + 7);
  const startOfWeekAfter = new Date(startOfThisWeek);
  startOfWeekAfter.setDate(startOfWeekAfter.getDate() + 14);
  const thisWeekKey = dateKeyOf(startOfThisWeek);
  const lastWeekKey = dateKeyOf(startOfLastWeek);
  const nextWeekKey = dateKeyOf(startOfNextWeek);
  const weekAfterKey = dateKeyOf(startOfWeekAfter);

  const q = useQuery({ queryKey: ["admin-posts"], queryFn: () => fetchPosts() });
  const allPosts = Array.isArray(q.data) ? q.data : [];
  const term = search.trim().toLowerCase();
  const posts = allPosts.filter((p: any) => {
    const okStatus = statusFilter === "all" ? true : p.status === statusFilter;
    const okSearch =
      !term ||
      (p.title ?? "").toLowerCase().includes(term) ||
      (p.slug ?? "").toLowerCase().includes(term);
    const plannedKey =
      typeof p.planned_date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(p.planned_date)
        ? p.planned_date
        : dateKeyOf(new Date(p.created_at ?? p.updated_at ?? 0));
    const okPeriod =
      periodFilter === "all"
        ? true
        : periodFilter === "this-week"
          ? plannedKey >= thisWeekKey && plannedKey < nextWeekKey
          : periodFilter === "next-week"
            ? plannedKey >= nextWeekKey && plannedKey < weekAfterKey
            : plannedKey >= lastWeekKey && plannedKey < thisWeekKey;
    const role = String(p.article_role ?? "").toLowerCase();
    const src = String(p.source ?? "").toLowerCase();
    const okLane =
      laneFilter === "all"
        ? true
        : laneFilter === "featured"
          ? Number(p.featured_rank ?? 0) > 0
          : laneFilter === "gsc"
            ? src.includes("gsc") || role === "gsc"
            : laneFilter === "pillar"
              ? role === "pillar" || src.includes("pillar")
              : role === "supporting" || src.includes("supporting");
    return okStatus && okSearch && okPeriod && okLane;
  });

  // Newest batch first; inside each batch, render its planned Sunday → Saturday dates.
  const dayOf = (p: any) => {
    const d = Number(p.planned_day);
    return Number.isFinite(d) && d >= 1 && d <= 7 ? d : 99;
  };
  const stampOf = (p: any) =>
    new Date(p.published_at ?? p.scheduled_at ?? p.created_at ?? p.updated_at ?? 0).getTime();
  posts.sort((a: any, b: any) => {
    const batchA = a.batch_week ?? "";
    const batchB = b.batch_week ?? "";
    if (batchA !== batchB) return batchA < batchB ? 1 : -1;
    const dateA = a.planned_date ?? "9999-12-31";
    const dateB = b.planned_date ?? "9999-12-31";
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    if (dayOf(a) !== dayOf(b)) return dayOf(a) - dayOf(b);
    if (stampOf(a) !== stampOf(b)) return stampOf(b) - stampOf(a);
    return (a.title ?? "").localeCompare(b.title ?? "");
  });

  const groupedPosts = posts.reduce<Array<{ key: string; batchWeek: string | null; posts: any[] }>>((groups, post: any) => {
    const key = typeof post.planned_date === "string" && post.planned_date ? post.planned_date : "unassigned";
    const batchWeek = typeof post.batch_week === "string" ? post.batch_week : null;
    const current = groups.at(-1);
    if (current && current.key === key && current.batchWeek === batchWeek) {
      current.posts.push(post);
    } else {
      groups.push({ key, batchWeek, posts: [post] });
    }
    return groups;
  }, []);

  const allSelected = posts.length > 0 && selected.length === posts.length;
  const toggleAll = () => setSelected(allSelected ? [] : posts.map((p: any) => p.id));
  const toggleOne = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const runBulk = async (
    action: "publish" | "schedule_recommended" | "set_day" | "draft" | "archive" | "delete",
  ) => {
    if (!selected.length) return;
    if (action === "delete" && !confirm(`Delete ${selected.length} post(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    const t = toast.loading("Applying…");
    try {
      const r: any = await bulkAction({
        data: { ids: selected, action, ...(action === "set_day" ? { day: bulkDay } : {}) },
      });
      await qc.invalidateQueries({ queryKey: ["admin-posts"] });
      setSelected([]);
      toast.success(`${r?.updated ?? selected.length} post(s) updated`, { id: t });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Bulk action failed", { id: t });
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this post? This cannot be undone.")) return;
    await deletePost({ data: { id } });
    await qc.invalidateQueries({ queryKey: ["admin-posts"] });
  };

  const handleAiDraft = async () => {
    setAiBusy(true);
    const t = toast.loading("Drafting a fresh SEO article with AI…");
    try {
      // Server auto-picks a unique topic/category/angle when none supplied.
      const r = (await generateDraft({
        data: { generateHero: true },
      })) as any;
      await qc.invalidateQueries({ queryKey: ["admin-posts"] });
      toast.success("AI draft created", { id: t });
      if (r?.post?.id) void navigate({ to: "/admin/posts/$id", params: { id: r.post.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Draft failed", { id: t });
    } finally {
      setAiBusy(false);
    }
  };


  return (
    <div>
      <BlogSubNav />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Blog posts</h1>
          <p className="mt-1 text-sm text-muted-foreground">Draft, schedule, and publish articles.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleAiDraft}
            disabled={aiBusy}
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/15 disabled:opacity-60"
          >
            {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {aiBusy ? "Drafting…" : "Draft AI"}
          </button>
          <Link
            to="/admin/posts/new"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            <Plus className="h-4 w-4" /> New post
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or slug…"
            className="w-full rounded-full border border-border bg-background py-2 pl-9 pr-4 text-sm outline-none focus:border-primary"
          />
        </div>
        <div className="inline-flex rounded-full border border-border p-1">
          {(["all", "published", "scheduled", "draft", "archived"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-semibold capitalize transition ${
                statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-full border border-border p-1">
          {([
            ["all", "All time"],
            ["this-week", "This week"],
            ["next-week", "Next week"],
            ["last-week", "Last week"],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setPeriodFilter(value)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                periodFilter === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-full border border-border p-1">
          {([
            ["all", "All types"],
            ["pillar", "Pillar"],
            ["supporting", "Supporting"],
            ["gsc", "Search Console"],
            ["featured", "Featured"],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setLaneFilter(value)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                laneFilter === value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3">
          <span className="text-sm font-semibold text-primary">{selected.length} selected</span>
          <button disabled={bulkBusy} onClick={() => runBulk("publish")} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-60">Publish now</button>
          <button disabled={bulkBusy} onClick={() => runBulk("schedule_recommended")} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-60">Schedule at recommended time</button>
          <div className="inline-flex items-center gap-1">
            <select
              value={bulkDay}
              onChange={(e) => setBulkDay(Number(e.target.value))}
              className="rounded-full border border-border bg-background px-3 py-1 text-xs"
              aria-label="Posting day"
            >
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <option key={d} value={d}>{DAY_LABELS[d]}</option>
              ))}
            </select>
            <button disabled={bulkBusy} onClick={() => runBulk("set_day")} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-60">Set day</button>
          </div>
          <button disabled={bulkBusy} onClick={() => runBulk("draft")} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-60">Back to draft</button>
          <button disabled={bulkBusy} onClick={() => runBulk("archive")} className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-muted disabled:opacity-60">Archive</button>
          <button disabled={bulkBusy} onClick={() => runBulk("delete")} className="rounded-full border border-destructive/40 px-3 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60">Delete</button>
          <button onClick={() => setSelected([])} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Clear</button>
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all posts" />
              </th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Post day</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>

          <tbody>
            {q.isLoading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
            ) : q.error ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-destructive">Couldn't load posts.</td></tr>
            ) : posts.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  {allPosts.length === 0 ? "No posts yet. Click New post to create one." : "No matches."}
                </td>
              </tr>
            ) : (
              groupedPosts.map((group) => (
                <Fragment key={`${group.batchWeek ?? "none"}-${group.key}`}>
                  <tr className="border-t border-border bg-primary/5">
                    <th colSpan={6} scope="rowgroup" className="px-4 py-2 text-left">
                      <div className="flex min-w-0 items-baseline gap-2">
                        <span className="text-xs font-bold uppercase text-primary">
                          {group.key === "unassigned"
                            ? "Unassigned"
                            : formatPlannedDate(group.key, { weekday: "long", month: "short", day: "numeric" })}
                        </span>
                        {group.batchWeek && (
                          <span className="truncate text-[11px] font-normal text-muted-foreground">
                            week of {group.batchWeek}
                          </span>
                        )}
                      </div>
                    </th>
                  </tr>
                  {group.posts.map((p: any) => (
                <tr key={p.id} className="border-t border-border bg-background">
                  <td className="px-4 py-3 align-top">
                    <input
                      type="checkbox"
                      checked={selected.includes(p.id)}
                      onChange={() => toggleOne(p.id)}
                      aria-label={`Select ${p.title || p.slug}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link to="/admin/posts/$id" params={{ id: p.id }} className="font-medium hover:text-primary">
                      {p.title || <span className="italic text-muted-foreground">Untitled</span>}
                    </Link>
                    <div className="text-xs text-muted-foreground">/blog/{p.slug}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {p.article_role === "pillar" && (
                        <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                          Pillar
                        </span>
                      )}
                      {p.article_role === "supporting" && (
                        <span className="rounded-full bg-accent/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-foreground">
                          Supporting
                        </span>
                      )}
                      {typeof p.source === "string" && p.source.includes("gsc") && (
                        <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
                          GSC
                        </span>
                      )}
                      {typeof p.source === "string" && p.source.startsWith("weekly-cluster") && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Weekly batch
                        </span>
                      )}
                      {typeof p.seo_score === "number" && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            p.seo_score >= 90
                              ? "bg-green-500/15 text-green-700"
                              : p.seo_score >= 70
                                ? "bg-yellow-500/15 text-yellow-700"
                                : "bg-destructive/15 text-destructive"
                          }`}
                        >
                          SEO {p.seo_score}
                        </span>
                      )}
                      {Number((p as any).featured_rank ?? 0) > 0 && (
                        <span
                          title={
                            (p as any).featured_evidence?.reason ??
                            "One of this week's two strongest opportunities."
                          }
                          className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
                        >
                          Featured this week
                        </span>
                      )}
                      {(p as any).owner_edited_at && (
                        <span
                          title="You edited this post by hand. Automation will not overwrite it."
                          className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          Your edit
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      // Only posts with a real planned calendar date get a day
                      // badge; older drafts that were never dated show as
                      // unassigned instead of squatting on a weekday.
                      const pd = (p as any).planned_date as string | null | undefined;
                      if (!pd) {
                        return <span className="text-xs text-muted-foreground">Unassigned</span>;
                      }
                      const day = plannedDayFor(p);
                      const slot = day ? recommendedSlot(day) : null;
                      const label = formatPlannedDate(pd, { weekday: "short", month: "short", day: "numeric" });
                      // Prefer the recommended time actually stored on the
                      // post, and show why it was chosen.
                      const rationale = (p as any).schedule_rationale?.rationale as string | undefined;
                      const recTime = (p as any).scheduled_at
                        ? new Date((p as any).scheduled_at).toLocaleTimeString("en-US", {
                            timeZone: "America/New_York",
                            hour: "numeric",
                            minute: "2-digit",
                          })
                        : slot?.time ?? "";
                      return (
                        <span
                          title={rationale ?? (slot ? `${slot.label} ${slot.time} ET — ${slot.why}` : undefined)}
                          className="rounded-full bg-purple-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-purple-700"
                        >
                          {label}{recTime ? ` ${recTime} ET` : ""}
                        </span>
                      );
                    })()}
                  </td>

                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${
                      p.status === "published" ? "bg-green-500/15 text-green-700"
                      : p.status === "scheduled" ? "bg-blue-500/15 text-blue-700"
                      : p.status === "archived" ? "bg-muted text-muted-foreground"
                      : "bg-yellow-500/15 text-yellow-700"
                    }`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(p.updated_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      {p.status === "published" && (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" title="View live">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button onClick={() => handleDelete(p.id)} className="text-muted-foreground hover:text-destructive" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                  ))}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
